import React from 'react';
import { UserPlus, Shuffle, Sheet, Megaphone, Inbox, FileUp } from 'lucide-react';

const METHOD = {
  manual: { label: 'Assigned manually', Icon: UserPlus, cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  round_robin: { label: 'Assigned by round-robin', Icon: Shuffle, cls: 'text-sky-700 bg-sky-50 border-sky-200' },
  connector_round_robin: { label: 'Connector round-robin', Icon: Sheet, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  connector_fixed_agent: { label: 'Connector → fixed agent', Icon: Sheet, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  unassigned: { label: 'Went to pool (unassigned)', Icon: Inbox, cls: 'text-slate-600 bg-slate-50 border-slate-200' },
};

// Works for new leads (explicit stamp fields) and legacy leads (derived from source/history).
export function deriveOrigin(lead) {
  if (!lead) return null;
  const first = (lead.assignment_history || []).find(h => h?.date) || null;
  let type = lead.origin_type, createdBy = lead.created_by_name, method = lead.assignment_method, assignedBy = lead.assigned_by_name;
  if (!type) {
    if (lead.lead_source === 'google_sheets' || first?.action === 'assigned_from_sheet_sync') { type = 'sheet_connector'; createdBy = createdBy || 'Sheet connector'; method = method || (first?.action === 'assigned_from_sheet_sync' ? 'connector_round_robin' : null); }
    else if (lead.lead_source === 'meta_ads' || lead.meta_lead_id) { type = 'meta_ads'; createdBy = createdBy || 'Meta Ads connector'; }
    else if (lead.lead_source === 'bulk_import' || lead.import_batch_id) { type = 'import'; createdBy = createdBy || 'Bulk import'; }
    else { type = 'manual'; createdBy = createdBy || first?.by_user_name || null; }
  }
  if (!method && type === 'manual') method = first?.action?.includes('round_robin') ? 'round_robin' : (first?.by_user_name ? 'manual' : null);
  if (!assignedBy && method === 'manual') assignedBy = first?.by_user_name || createdBy;
  const legacy = !lead.origin_type;
  return { type, createdBy, method, assignedBy, legacy, createdAt: lead.created_at };
}

const TYPE_LABEL = { manual: 'Added manually', sheet_connector: 'Google Sheet connector', meta_ads: 'Meta Ads', import: 'Bulk import' };
const TYPE_ICON = { manual: UserPlus, sheet_connector: Sheet, meta_ads: Megaphone, import: FileUp };

export function LeadOriginStamp({ lead, compact = false }) {
  const o = deriveOrigin(lead);
  if (!o) return null;
  const m = METHOD[o.method];
  const TIcon = TYPE_ICON[o.type] || UserPlus;
  if (compact) {
    const short = { manual: 'Manual', sheet_connector: 'Sheet', meta_ads: 'Meta', import: 'Import' }[o.type] || o.type;
    const ms = o.method === 'manual' ? `picked${o.assignedBy ? ` by ${o.assignedBy.trim().split(' ')[0]}` : ''}` : o.method?.includes('round_robin') ? 'RR' : o.method === 'connector_fixed_agent' ? 'fixed' : o.method === 'unassigned' ? 'pool' : '';
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] text-muted-foreground" title={`${TYPE_LABEL[o.type]}${o.createdBy ? ` by ${o.createdBy}` : ''}${m ? ` · ${m.label}` : ''}`} data-testid={`lead-origin-${lead.id}`}>
        <TIcon className="h-3 w-3" />{o.type === 'manual' && o.createdBy ? o.createdBy.trim().split(' ')[0] : short}{ms && ` · ${ms}`}
      </span>
    );
  }
  return (
    <div className="col-span-2 rounded-md border bg-muted/40 p-3 text-xs space-y-1" data-testid="lead-origin-stamp">
      <div className="font-semibold uppercase tracking-wide text-muted-foreground">Lead origin</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5"><TIcon className="h-3.5 w-3.5" /><b>{TYPE_LABEL[o.type] || o.type}</b>{o.createdBy && <> by <b>{o.createdBy}</b></>}{lead.created_by_role && lead.created_by_role !== 'system' && <span className="text-muted-foreground">({lead.created_by_role.replace(/_/g, ' ')})</span>}</span>
        {m && <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${m.cls}`}><m.Icon className="h-3 w-3" />{m.label}{o.method === 'manual' && o.assignedBy && <> by {o.assignedBy}</>}</span>}
        {!m && <span className="text-muted-foreground">Assignment method not recorded</span>}
      </div>
      {o.legacy && <div className="text-[10px] text-muted-foreground">Derived from history — this lead predates origin stamping.</div>}
    </div>
  );
}
